import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// App-wide horizontal scroll slider for wide lists, same behavior as the
// AiventraLMS Table (lms-tms-app/frontend/src/components/ui/Table.jsx):
//
// - shown only when a table really is wider than its box;
// - while that table runs on below the bottom of the visible area (so its own
//   scrollbar is out of sight), a slider is pinned to the bottom of the screen,
//   lined up with the table's own left edge and width;
// - once the table's end comes into view the pinned slider goes away and the
//   table's own native scrollbar, now on screen, takes over.
//
// Mounted once in Layout. It finds every <table>'s horizontally scrolling
// wrapper itself, so pages and modals need no wiring.

const isHScroller = (el) => {
  const ox = getComputedStyle(el).overflowX;
  return ox === 'auto' || ox === 'scroll';
};

const isVScroller = (el) => {
  const oy = getComputedStyle(el).overflowY;
  return (oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1;
};

const findHScroller = (table) => {
  for (let el = table.parentElement; el && el !== document.body; el = el.parentElement) {
    if (isHScroller(el)) return el;
  }
  return null;
};

// Bottom edge of the area the element can actually be seen in: the nearest
// vertically scrolling ancestor (main content area, a modal body) or the
// window, minus the tablet bottom nav when it's showing.
const visibleBounds = (el) => {
  const nav = document.querySelector('.tablet-bottom-nav');
  const navH = nav && getComputedStyle(nav).display !== 'none' ? nav.getBoundingClientRect().height : 0;
  let top = 0;
  let bottom = window.innerHeight - navH;
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    if (isVScroller(p)) {
      const r = p.getBoundingClientRect();
      top = Math.max(top, r.top);
      bottom = Math.min(bottom, r.bottom);
      break;
    }
  }
  return { top, bottom };
};

export default function StickyHScrollbar() {
  const [bar, setBar] = useState(null); // { left, width, bottom, thumbLeft, thumbWidth, z }
  const targetRef = useRef(null);
  const trackRef = useRef(null);
  const frame = useRef(0);

  const measure = useCallback(() => {
    frame.current = 0;
    const scrollers = new Set();
    document.querySelectorAll('table').forEach((t) => {
      const s = findHScroller(t);
      if (s && s.scrollWidth > s.clientWidth + 2) scrollers.add(s);
    });

    let pick = null;
    scrollers.forEach((s) => {
      const r = s.getBoundingClientRect();
      if (!r.width) return;
      const { top, bottom } = visibleBounds(s);
      const showing = r.top < bottom - 40 && r.bottom > top;
      const endBelowFold = r.bottom > bottom + 2;
      if (!showing || !endBelowFold) return;
      const inModal = !!s.closest('.modal-overlay');
      // A list in an open modal wins over the page behind it; among equals
      // the last one in document order is the one stacked on top.
      if (!pick || inModal || !pick.inModal) pick = { s, r, bottom, inModal };
    });

    if (!pick) {
      targetRef.current = null;
      setBar(null);
      return;
    }
    const { s, r, bottom, inModal } = pick;
    targetRef.current = s;
    const widthPct = Math.min(100, (s.clientWidth / s.scrollWidth) * 100);
    const maxScroll = s.scrollWidth - s.clientWidth;
    const leftPct = maxScroll > 0 ? (s.scrollLeft / maxScroll) * (100 - widthPct) : 0;
    const next = {
      left: r.left + s.clientLeft,
      width: s.clientWidth,
      bottom: window.innerHeight - bottom + 8,
      thumbLeft: leftPct,
      thumbWidth: widthPct,
      z: inModal ? 1400 : 900,
    };
    setBar((prev) => (prev && Object.keys(next).every((k) => Math.abs(prev[k] - next[k]) < 0.5) ? prev : next));
  }, []);

  const schedule = useCallback(() => {
    if (!frame.current) frame.current = requestAnimationFrame(measure);
  }, [measure]);

  useEffect(() => {
    schedule();
    // Capture phase: scroll events don't bubble, and this catches both the
    // vertical scroll of the content area and each table's own sideways scroll.
    document.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      document.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      mo.disconnect();
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [schedule]);

  const onPointerDown = (e) => {
    const track = trackRef.current;
    const el = targetRef.current;
    if (!track || !el || !bar) return;
    e.preventDefault();
    const rect = track.getBoundingClientRect();
    const thumbPx = (bar.thumbWidth / 100) * rect.width;
    const moveTo = (clientX) => {
      const x = clientX - rect.left - thumbPx / 2;
      const maxX = rect.width - thumbPx;
      const ratio = maxX > 0 ? Math.min(1, Math.max(0, x / maxX)) : 0;
      el.scrollLeft = ratio * (el.scrollWidth - el.clientWidth);
    };
    moveTo(e.clientX);
    const onMove = (ev) => moveTo(ev.clientX);
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  if (!bar) return null;
  return createPortal(
    <div
      ref={trackRef}
      onPointerDown={onPointerDown}
      className="lms-hscroll-track no-print"
      style={{ position: 'fixed', left: bar.left, width: bar.width, bottom: bar.bottom, zIndex: bar.z }}
      aria-hidden="true"
    >
      <div className="lms-hscroll-thumb" style={{ left: `${bar.thumbLeft}%`, width: `${bar.thumbWidth}%` }} />
    </div>,
    document.body
  );
}
